import React, { useEffect, useRef, useState } from 'react';
import { Tile } from '../tile';
import { chartTokens } from '../charts/chart-tokens';
import { formatCompact } from '../charts/chart-utils';
import './volume-by-type-chart.scss';

export interface VolumeByTypeRow {
  category: string;
  automated: number;
  manual: number;
  total: number;
}

export interface VolumeByTypeChartProps {
  rows: VolumeByTypeRow[];
  maxRows?: number;
  ariaLabel?: string;
}

interface RowBarProps {
  row: VolumeByTypeRow;
  maxTotal: number;
  hovered: boolean;
  onEnter: () => void;
  onLeave: () => void;
  /** Animation progress 0→1 */
  progress: number;
}

const RowBar: React.FC<RowBarProps> = ({ row, maxTotal, hovered, onEnter, onLeave, progress }) => {
  const trackPct = maxTotal > 0 ? (row.total / maxTotal) * 100 * progress : 0;
  const autoPct = row.total > 0 ? (row.automated / row.total) * 100 : 0;
  const manPct = 100 - autoPct;
  const opacity = hovered ? 1 : 0.92;

  return (
    <div
      className="volume-by-type-chart__row"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      role="row"
      aria-label={`${row.category}: ${formatCompact(row.total)} total`}>
      <span className="volume-by-type-chart__row-label" role="rowheader">
        {row.category}
      </span>
      <div className="volume-by-type-chart__track" role="presentation">
        <div
          className="volume-by-type-chart__fill"
          style={{ width: `${trackPct}%`, opacity }}>
          <div
            className="volume-by-type-chart__fill-auto"
            style={{ width: `${autoPct}%` }}
          />
          <div
            className="volume-by-type-chart__fill-manual"
            style={{ width: `${manPct}%` }}
          />
        </div>
      </div>
      <span className="volume-by-type-chart__total" role="cell">
        {formatCompact(row.total)}
      </span>
    </div>
  );
};

export const VolumeByTypeChart: React.FC<VolumeByTypeChartProps> = ({
  rows,
  maxRows,
  ariaLabel = 'Volume by case type',
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const DURATION = 700;

  useEffect(() => {
    setProgress(0);
    startRef.current = null;
    const tick = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / DURATION);
      setProgress(p * (2 - p)); // ease-out
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [rows]);

  const visibleRows = maxRows ? rows.slice(0, maxRows) : rows;
  const maxTotal = Math.max(...visibleRows.map((r) => r.total), 1);
  const hoveredRow = hoveredIdx != null ? visibleRows[hoveredIdx] ?? null : null;

  const tooltip = hoveredRow ? (
    <div className="volume-by-type-chart__tooltip" role="tooltip" aria-live="polite">
      <strong>{hoveredRow.category}</strong>
      {' / '}
      <span>Auto {formatCompact(hoveredRow.automated)}</span>
      {' · '}
      <span>Manual {formatCompact(hoveredRow.manual)}</span>
    </div>
  ) : null;

  const legend = (
    <div className="volume-by-type-chart__legend" aria-hidden="true">
      {[
        { key: 'auto', label: 'Automated', color: chartTokens.series.automated },
        { key: 'manual', label: 'Manual', color: chartTokens.series.manual },
      ].map((s) => (
        <div key={s.key} className="volume-by-type-chart__legend-item">
          <span className="volume-by-type-chart__legend-swatch" style={{ background: s.color }} />
          <span className="volume-by-type-chart__legend-label">{s.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Tile
      title="Volume by Case Type"
      subtitle="Hover for category details"
      actions={legend}
      className="volume-by-type-chart-tile"
      ariaLabel={ariaLabel}>
      <div
        className="volume-by-type-chart__body"
        role="table"
        aria-label={ariaLabel}
        onMouseLeave={() => setHoveredIdx(null)}>
        {visibleRows.map((row, idx) => (
          <RowBar
            key={row.category}
            row={row}
            maxTotal={maxTotal}
            hovered={hoveredIdx === idx}
            onEnter={() => setHoveredIdx(idx)}
            onLeave={() => setHoveredIdx(null)}
            progress={progress}
          />
        ))}
        {tooltip}
      </div>
    </Tile>
  );
};

VolumeByTypeChart.displayName = 'VolumeByTypeChart';
